import { useState, useEffect, cloneElement } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { DatePicker } from '@material-ui/pickers';
import { useUtils } from '@material-ui/pickers';
import InputAdornment from '@material-ui/core/InputAdornment';
import CalendarIcon from './icons/Calendar';
import TextField from '../TextField';

const useStyles = makeStyles(
  ({ palette }) => ({
    '@global': {
      '.MuiPickersBasePicker-pickerView': {
        minWidth: 248,
        minHeight: 292,
      },
      '.MuiPickersCalendarHeader-dayLabel': {
        color: palette.cncUI.primary.gray['+6'],
        width: 32,
        height: 24,
        margin: '4px 0',
        fontSize: 12,
        lineHeight: '16px',
        textAlign: 'center',
        letterSpacing: '0.32px',
      },
      '.MuiPickersDay-day': {
        color: palette.cncUI.default.primary,
        width: 32,
        height: 32,
        margin: '4px 0',
        borderRadius: 0,
        '&:hover': {
          borderRadius: 0,
        },
        '& .MuiTypography-root': {
          fontSize: 14,
          lineHeight: '18px',
          textAlign: 'center',
          letterSpacing: '0.16px',
        },
      },
      '.MuiPickersDay-current': {
        position: 'relative',
        '&:before': {
          content: "' '",
          display: 'block',
          width: 4,
          height: 4,
          background: palette.cncUI.default.primary,
          position: 'absolute',
          bottom: 0,
        },
        '&.MuiPickersDay-hidden:before': {
          background: palette.cncUI.primary.gray['+7'],
        },
      },
      '.MuiPickersDay-daySelected': {
        borderRadius: 0,
        backgroundColor: palette.cncUI.default['+7'],
        '&:hover': {
          backgroundColor: palette.cncUI.default['+7'],
        },
      },
      '.MuiPickersDay-dayRangeStart': {
        background: palette.cncUI.default.primary,
        color: palette.cncUI.default['+10'],
        '&:hover': {
          background: palette.cncUI.default.primary,
          color: palette.cncUI.default['+10'],
        },
        '&:before': {
          background: palette.cncUI.default['+10'],
        },
      },
      '.MuiPickersDay-dayRangeEnd': {
        background: palette.cncUI.default.primary,
        color: palette.cncUI.default['+10'],
        '&:hover': {
          background: palette.cncUI.default.primary,
          color: palette.cncUI.default['+10'],
        },
        '&:before': {
          background: palette.cncUI.default['+10'],
        },
      },
      '.MuiPickersDay-hidden': {
        opacity: 1,
        color: palette.cncUI.primary.gray['+7'],
        background: 'transparent',
      },
    },
    root: {
      minWidth: 310,
      position: 'relative',
      '& .MuiSvgIcon-root': {
        cursor: 'pointer',
      },
      '& .MuiInputAdornment-positionStart': {
        position: 'absolute',
        left: 129,
        top: 18,
      },
      '& .MuiInputAdornment-positionEnd': {
        position: 'absolute',
        right: 8,
        top: 18,
      },
    },
    short: {
      minWidth: 275,
      '& .MuiInputAdornment-positionStart': {
        left: 115,
        top: 12,
      },
      '& .MuiInputAdornment-positionEnd': {
        right: 8,
        top: 12,
      },
    },
  }),
  { name: 'CncUI-DateRangePicker' },
);

const DateRangePicker = props => {
  const classes = useStyles();
  const {
    value,
    onChange,
    labelFunc,
    format = 'MM / dd / yyyy',
    emptyLabel,
    autoOk = true,
    short = false,
    onOpen,
    onClose,
    className,
    open: openForward,
    ...other
  } = props;
  const [prevBegin, setPrevBegin] = useState(undefined);
  const [prevEnd, setPrevEnd] = useState(undefined);
  const [hasClicked, setHasClicked] = useState(false);

  const [hover, setHover] = useState(undefined);
  const [accepted, setAccepted] = useState(value.begin && value.end);
  const utils = useUtils();

  const min = Math.min(value.begin, value.end || hover);
  const max = Math.max(value.begin, value.end || hover);

  const [open, setOpen] = useState(false);

  const isOpen = openForward !== undefined ? openForward : open;
  const setBegin = day => onChange({ begin: day });
  const setEnd = day => onChange({ end: day });
  const update = (begin, end) => onChange({ begin, end });

  useEffect(() => {
    //Only way to get to this state is is openForward is used
    if (isOpen && accepted && !prevBegin && !prevEnd) {
      setAccepted(false);
      setPrevBegin(value.begin);
      setPrevEnd(value.end);
      return;
    }
    //Closed without accepting, reset to prev state, don't find onChange
    if (!isOpen && !accepted) {
      update(prevBegin, prevEnd);
      setHover(undefined);
      setHasClicked(false);
    }
    //Auto ok and hasn't been accepted, but has all the items set, accept and close.
    //This will also triger the on change event by setting isOpen to false
    if (
      isOpen &&
      autoOk &&
      !accepted &&
      value.begin &&
      value.end &&
      hasClicked
    ) {
      setAccepted(true);
      onClose ? onClose() : setOpen(false);
    }
    if (accepted && value.begin && value.end && !isOpen && hasClicked) {
      setHasClicked(false);
      onChange(value);
      onClose ? onClose() : setOpen(false);
    }
  }, [autoOk, accepted, isOpen, prevBegin, hasClicked, prevEnd, value]);

  function renderDay(day, selectedDate, dayInCurrentMonth, dayComponent) {
    return cloneElement(dayComponent, {
      onClick: e => {
        setHasClicked(true);
        e.stopPropagation();
        if (!value.begin) setBegin(day);
        else if (!value.end) {
          if (utils.isBeforeDay(day, value.begin)) {
            update(day, value.begin);
          } else {
            setEnd(day);
          }
          if (autoOk) {
            setPrevBegin(undefined);
            setPrevEnd(undefined);
          }
        } else {
          update(day, undefined);
        }
      },
      onMouseEnter: () => requestAnimationFrame(() => setHover(day)),
      onFocus: () => requestAnimationFrame(() => setHover(day)),
      className: clsx('MuiPickersDay-day', {
        'MuiPickersDay-hidden': dayComponent.props.hidden,
        'MuiPickersDay-current': dayComponent.props.current,
        'MuiPickersDay-daySelected':
          utils.isAfterDay(day, min) && utils.isBeforeDay(day, max),
        'MuiPickersDay-dayRangeStart': utils.isSameDay(day, min),
        'MuiPickersDay-dayRangeEnd': utils.isSameDay(day, max),
      }),
    });
  }

  const formatDate = date => utils.format(date, format || utils.dateFormat);

  return (
    <DatePicker
      disableToolbar
      variant="inline"
      placeholder="MM / DD / YYYY        MM / DD / YYYY"
      {...other}
      short={short}
      className={clsx(
        'cncui-datepicker-range',
        classes.root,
        short && classes.short,
        className,
      )}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <CalendarIcon />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <CalendarIcon />
          </InputAdornment>
        ),
      }}
      TextFieldComponent={TextField}
      value={value.begin}
      renderDay={renderDay}
      open={isOpen}
      onOpen={() => {
        setAccepted(false);
        setPrevBegin(value.begin);
        setPrevEnd(value.end);
        onOpen ? onOpen() : setOpen(true);
      }}
      onAccept={() => {
        if (!value.begin || !value.end) {
          if (hover && utils.isBeforeDay(value.begin, hover)) {
            setEnd(hover);
          } else {
            update(hover, value.begin);
          }
        }
        setPrevBegin(undefined);
        setPrevEnd(undefined);
        // if (!autoOk) {
        setAccepted(true);
        // }
      }}
      onClose={() => {
        onClose ? onClose() : setOpen(false);
      }}
      onChange={() => {}}
      labelFunc={(date, invalid) =>
        !isOpen
          ? labelFunc
            ? labelFunc(value, invalid)
            : date && value.begin && value.end
            ? `   ${formatDate(value.begin)}          ${formatDate(value.end)}`
            : emptyLabel || ''
          : prevBegin && prevEnd
          ? labelFunc
            ? labelFunc({ begin: prevBegin, end: prevEnd }, invalid)
            : `   ${formatDate(prevBegin)}          ${formatDate(prevEnd)}`
          : emptyLabel || ''
      }
    />
  );
};

export default DateRangePicker;
